/**
 * Desconto por volume (tabela descontoproduto) - mesma regra do backend
 * (App\Services\DescontoProdutoService), usada só para a prévia na tela.
 * Quem grava o preço com desconto é sempre o backend.
 *
 *  - Cada linha aponta para um produto (codproduto) ou, sem ele, para um grupo
 *    (codgrupo). As linhas da mesma promoção (prefixo do codigo, ver
 *    codigoPromocao) com a mesma quantidade e desconto formam uma
 *    só faixa: o volume é a soma de todos os produtos/grupos dela (ex.: grupos
 *    0002 e 0003 com 500 un. -> 10 do 0002 + 20 do 0003 = 30 para a faixa).
 *  - A faixa é atingida quando essa soma chega em quantidade.
 *  - Cada item recebe no máximo uma faixa: a que cita o produto tem prioridade
 *    sobre a que cita só o grupo; entre as atingidas vale a de maior desconto.
 *
 * @param {Array<{chave, produto, grupo, qtd}>} itens
 * @param {Array<{codigo, descricao, codproduto, codgrupo, quantidade, desconto}>} regras
 * @returns {{itens: Object<string, Object|null>, regras: Array}}
 */
export function calculaDescontoVolume(itens, regras) {
  const txt = (v) => (v === null || v === undefined ? "" : String(v).trim());
  const lista = Array.isArray(itens) ? itens : [];

  const faixas = [];
  const porChave = {};
  (Array.isArray(regras) ? regras : []).forEach((regra) => {
    const prod = txt(regra.codproduto);
    const grp = prod ? "" : txt(regra.codgrupo);
    if (!prod && !grp) {
      return;
    }
    const quantidade = parseFloat(regra.quantidade) || 0;
    const desconto = parseFloat(regra.desconto) || 0;
    const codigo = codigoPromocao(regra.codigo);
    const chave = `${codigo}|${quantidade}|${desconto}`;
    if (!porChave[chave]) {
      porChave[chave] = { ...regra, codigo, chave, quantidade, desconto, produtos: [], grupos: [] };
      faixas.push(porChave[chave]);
    }
    const faixa = porChave[chave];
    if (prod && !faixa.produtos.includes(prod)) faixa.produtos.push(prod);
    if (grp && !faixa.grupos.includes(grp)) faixa.grupos.push(grp);
  });

  // 'produto' quando a faixa cita o produto do item, 'grupo' quando só o grupo
  const casamento = (faixa, item) => {
    if (faixa.produtos.includes(txt(item.produto))) return "produto";
    const grp = txt(item.grupo);
    return grp && faixa.grupos.includes(grp) ? "grupo" : null;
  };

  const situacao = faixas.map((faixa) => {
    const qtdatual = lista.reduce((soma, item) => soma + (casamento(faixa, item) ? parseFloat(item.qtd) || 0 : 0), 0);
    const tipo = faixa.produtos.length && faixa.grupos.length ? "misto" : faixa.produtos.length ? "produto" : "grupo";
    return {
      ...faixa,
      tipo,
      alvo: [...faixa.produtos, ...faixa.grupos].join(", "),
      alvolabel: rotuloAlvo(faixa.produtos, faixa.grupos),
      qtdatual,
      atingida: qtdatual > 0 && qtdatual >= faixa.quantidade,
      falta: Math.max(0, faixa.quantidade - qtdatual),
    };
  });

  const porItem = {};
  lista.forEach((item) => {
    let melhor = null;
    let melhorPorProduto = false;
    situacao.forEach((faixa) => {
      if (!faixa.atingida) return;
      const tipo = casamento(faixa, item);
      if (!tipo) return;
      const porProduto = tipo === "produto";
      if (!melhor || (porProduto && !melhorPorProduto)
          || (porProduto === melhorPorProduto && faixa.desconto > melhor.desconto)) {
        melhor = faixa;
        melhorPorProduto = porProduto;
      }
    });
    porItem[item.chave] = melhor;
  });

  return { itens: porItem, regras: situacao };
}

/**
 * O codigo vem como <promoção>_<produto>_<grupo> (ex.: 000002__0004); a
 * promoção é o que vem antes do primeiro "_".
 */
export function codigoPromocao(codigo) {
  const cod = codigo === null || codigo === undefined ? "" : String(codigo).trim();
  return cod.split("_")[0] || cod;
}

export function rotuloAlvo(produtos, grupos) {
  const partes = [];
  if (grupos.length) partes.push(`${grupos.length > 1 ? "grupos" : "grupo"} ${grupos.join(", ")}`);
  if (produtos.length) partes.push(`${produtos.length > 1 ? "produtos" : "produto"} ${produtos.join(", ")}`);
  return partes.join(" + ");
}

/**
 * Arredonda em 2 casas como o round() do PHP: 14,265 -> 14,27 (Math.round puro
 * daria 14,26, porque 14.265 * 100 = 1426.4999999999998).
 */
export function arredonda2(valor) {
  const n = parseFloat(valor) || 0;
  return Math.round(Number((n * 100).toPrecision(15))) / 100;
}

/**
 * Total com desconto, mesma regra do backend: o unitário com desconto é
 * arredondado em 2 casas e o total é qtd x esse unitário (96 x 14,27 = 1.369,92).
 */
export function totalComDesconto(qtd, vlrunit, perc) {
  return arredonda2(qtd * arredonda2(vlrunit * (1 - perc / 100)));
}
